import { useState, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import type { LlmPreset } from '@/types';

// Preset options with display info
const PRESET_OPTIONS: {
  value: LlmPreset;
  label: string;
  provider: 'anthropic' | 'openai' | 'google';
  description: string;
}[] = [
  // Anthropic
  { value: 'claude-opus', label: 'Claude Opus', provider: 'anthropic', description: '최고 성능' },
  { value: 'claude-sonnet', label: 'Claude Sonnet', provider: 'anthropic', description: '균형 잡힌 성능' },
  { value: 'claude-haiku', label: 'Claude Haiku', provider: 'anthropic', description: '빠른 응답' },
  // OpenAI
  { value: 'gpt-pro', label: 'GPT Pro', provider: 'openai', description: '최고 성능' },
  { value: 'gpt-standard', label: 'GPT Standard', provider: 'openai', description: '일반 작업' },
  { value: 'gpt-chat', label: 'GPT Chat', provider: 'openai', description: '대화 최적화' },
  { value: 'gpt-mini', label: 'GPT Mini', provider: 'openai', description: '빠른 응답' },
  // Google
  { value: 'gemini-pro', label: 'Gemini Pro', provider: 'google', description: '복잡한 추론' },
  { value: 'gemini-flash', label: 'Gemini Flash', provider: 'google', description: '빠르고 효율적' },
];

// Provider colors
const PROVIDER_COLORS: Record<string, string> = {
  anthropic: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  openai: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  google: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
};

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
};

interface LlmPresetSelectorProps {
  className?: string;
}

export function LlmPresetSelector({ className }: LlmPresetSelectorProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { user, updateLlmPreset } = useAuthStore();

  const currentPreset = user?.effectiveLlmPreset || 'claude-sonnet';
  const currentOption = PRESET_OPTIONS.find((opt) => opt.value === currentPreset);

  const handleSelect = useCallback(
    async (preset: string) => {
      if (preset === currentPreset) {
        return;
      }

      setIsUpdating(true);
      try {
        await updateLlmPreset(preset as LlmPreset);
      } catch (error) {
        console.error('Failed to update preset:', error);
      } finally {
        setIsUpdating(false);
      }
    },
    [currentPreset, updateLlmPreset]
  );

  // Group presets by provider
  const groupedPresets = PRESET_OPTIONS.reduce(
    (acc, preset) => {
      if (!acc[preset.provider]) {
        acc[preset.provider] = [];
      }
      acc[preset.provider].push(preset);
      return acc;
    },
    {} as Record<string, typeof PRESET_OPTIONS>
  );

  return (
    <Select value={currentPreset} onValueChange={handleSelect} disabled={isUpdating}>
      <SelectTrigger className={cn('h-8 w-[160px] gap-1 px-2', className)}>
        <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
        <SelectValue>
          <span className="truncate text-xs">{currentOption?.label || 'Select model'}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {Object.entries(groupedPresets).map(([provider, presets]) => (
          <SelectGroup key={provider}>
            <SelectLabel>
              <span className={cn('rounded px-1.5 py-0.5 text-xs', PROVIDER_COLORS[provider])}>
                {PROVIDER_LABELS[provider]}
              </span>
            </SelectLabel>
            {presets.map((preset) => (
              <SelectItem key={preset.value} value={preset.value}>
                <div className="flex flex-col">
                  <span className="text-sm">{preset.label}</span>
                  <span className="text-xs text-muted-foreground">{preset.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
